import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, FileText } from 'lucide-react';

interface Publication {
  id: number;
  title: string;
  authors: string;
  journal: string;
  year: number;
  doi?: string;
  abstract: string;
  keywords: string[];
  previewImage?: string;
}

const ResearchSection: React.FC = () => {
  const publications: Publication[] = [
    {
      id: 6,
      title: "TPR: A Training Procedure Representation to Augment XR Simulations with LLMs",
      authors: "M Guevarra, C Wayllace, S Das, CD Epp, A Tay",
      journal: "Proceedings of the AAAI Conference on Artificial Intelligence",
      year: 2026,
      doi: "https://doi.org/10.1609/aaai.v40i48.42350",
      abstract: "Extended reality (XR) is well suited to support the situated learning of technical procedures. At the same time, AI-driven intelligent tutoring systems (ITS) can complement XR by providing adaptive pedagogical support. We present a domain-agnostic XR-based ITS that integrates a training procedure representation (TPR), XR simulation, and an LLM-driven instructor, demonstrated for tissue sample handling and engine repair.",
      keywords: ["Extended Reality", "Large Language Models", "Intelligent Tutoring Systems", "XR Simulations"],
      previewImage: "/research/2026_vr.png"
    },
    {
        id: 1,
        title: "An LLM-Guided Tutoring System for Social Skills Training",
        authors: "M Guevarra, I Bhattacharjee, S Das, C Wayllace, CD Epp, ME Taylor",
        journal: "Proceedings of the AAAI Conference on Artificial Intelligence",
        year: 2025,
        doi: "https://doi.org/10.1609/aaai.v39i28.35353",
        abstract: "This research introduces a large language model-based tutoring system designed to enhance social skills development through personalized feedback and interactive learning experiences.",
        keywords: ["Large Language Models", "Social Skills", "Tutoring Systems", "Personalized Learning"],
        previewImage: "/research/2025_interface.png"
      },
      {
      id: 2,
      title: "Augmenting flight training with AI to efficiently train pilots",
      authors: "M Guevarra, S Das, C Wayllace, CD Epp, M Taylor, A Tay",
      journal: "Proceedings of the AAAI Conference on Artificial Intelligence",
      year: 2023,
      doi: "https://doi.org/10.1609/aaai.v37i13.27071",
      abstract: "We present an AI-driven approach to enhance pilot training efficiency through intelligent feedback systems and adaptive learning methodologies in flight simulation environments.",
      keywords: ["AI Training", "Flight Simulation", "Pilot Training", "Adaptive Learning"],
      previewImage: "/research/2023_feedback.png"
    },
    {
      id: 3,
      title: "Pilot Trainees Benefit from Modelling and Adaptive Feedback",
      authors: "YA Abdullah, M Guevarra, M Cai, J Yan, ME Taylor, C Demmans Epp",
      journal: "Proceedings of the 33rd ACM Conference on User Modeling, Adaptation and Personalization",
      year: 2025,
      doi: "https://doi.org/10.1145/3699682.3728337",
      abstract: "We demonstrate how modeling techniques combined with adaptive feedback mechanisms significantly improve pilot training outcomes and skill development.",
      keywords: ["User Modeling", "Adaptive Feedback", "Pilot Training", "Skill Development"]
    },
    {
        id: 4,
        title: "Population based equilibrium in hybrid sa/pso for combinatorial optimization: hybrid sa/pso for combinatorial optimization",
        authors: "K Brezinski, M Guevarra, K Ferens",
        journal: "International Journal of Software Science and Computational Intelligence",
        year: 2020,
        doi: "https://doi.org/10.4018/IJSSCI.2020040105",
        abstract: "This paper explores hybrid simulated annealing and particle swarm optimization approaches for solving complex combinatorial optimization problems, achieving improved convergence and solution quality.",
        keywords: ["Combinatorial Optimization", "Simulated Annealing", "Particle Swarm Optimization", "Hybrid Algorithms"]
      },
    {
      id: 5,
      title: "Reinforcement learning agents providing real-time formative feedback to users in training simulations",
      authors: "M Guevarra",
      journal: "Masters Thesis",
      year: 2023,
      abstract: "This thesis explores the development and implementation of reinforcement learning agents that provide real-time formative feedback to users in training simulations, with applications in aviation and other domains.",
      keywords: ["Reinforcement Learning", "Formative Feedback", "Training Simulations", "Real-time Systems"]
    }
  ];

  const [currentPage, setCurrentPage] = useState(0);
  const publicationsPerPage = 3;
  const totalPages = Math.ceil(publications.length / publicationsPerPage);

  const nextPage = () => {
    setCurrentPage((prev) => (prev + 1) % totalPages);
  };

  const prevPage = () => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
  };

  const currentPublications = publications.slice(
    currentPage * publicationsPerPage,
    (currentPage + 1) * publicationsPerPage
  );

  return (
    <section id="research-section" className="bg-white/40 section-padding">
      <div className="container-max">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-6">
            Research & Publications
          </h2>
          <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
            My research focuses on AI-driven training systems, reinforcement learning for real-time feedback, 
            and adaptive learning methodologies to enhance skill development in aviation and educational contexts.
          </p>
        </div>

        {/* Publications Grid Carousel */}
        <div className="relative">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
            {currentPublications.map((publication) => (
              <div key={publication.id} className="bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                {publication.previewImage && (
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={publication.previewImage}
                      alt={`Preview of ${publication.title}`}
                      className="w-full h-full object-cover"
                    />
                    <span className="absolute top-3 right-3 text-sm font-medium text-primary-600 bg-primary-50/90 backdrop-blur-sm px-3 py-1 rounded-full">
                      {publication.year}
                    </span>
                  </div>
                )}

                <div className="p-6">
                  {!publication.previewImage && (
                    <div className="flex items-start justify-between mb-4">
                      <div className="p-2 bg-primary-100 rounded-lg">
                        <FileText className="h-6 w-6 text-primary-600" />
                      </div>
                      <span className="text-sm font-medium text-primary-600 bg-primary-50 px-3 py-1 rounded-full">
                        {publication.year}
                      </span>
                    </div>
                  )}

                <h3 className="text-lg font-semibold text-secondary-900 mb-3 line-clamp-3">
                  {publication.title}
                </h3>
                
                <p className="text-sm text-secondary-600 mb-3">
                  <span className="font-medium">Authors:</span> {publication.authors}
                </p>
                
                <p className="text-sm text-secondary-600 mb-4">
                  <span className="font-medium">Journal:</span> {publication.journal}
                </p>
                
                <p className="text-sm text-secondary-600 mb-4 line-clamp-3">
                  {publication.abstract}
                </p>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {publication.keywords.slice(0, 3).map((keyword) => (
                    <span
                      key={keyword}
                      className="px-2 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full"
                    >
                      {keyword}
                    </span>
                  ))}
                  {publication.keywords.length > 3 && (
                    <span className="px-2 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full">
                      +{publication.keywords.length - 3} more
                    </span>
                  )}
                </div>
                
                {publication.doi && (
                  <a
                    href={publication.doi}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium text-sm transition-colors duration-200"
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    View Publication
                  </a>
                )}
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Controls */}
          <div className="flex justify-center items-center space-x-4">
            <button
              onClick={prevPage}
              className="p-3 bg-white/80 backdrop-blur-sm rounded-full shadow-lg hover:shadow-xl transition-shadow duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={totalPages <= 1}
            >
              <ChevronLeft className="h-6 w-6 text-secondary-600" />
            </button>
            
            <div className="flex space-x-2">
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i)}
                  className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                    i === currentPage ? 'bg-primary-600' : 'bg-secondary-300'
                  }`}
                />
              ))}
            </div>
            
            <button
              onClick={nextPage}
              className="p-3 bg-white/80 backdrop-blur-sm rounded-full shadow-lg hover:shadow-xl transition-shadow duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={totalPages <= 1}
            >
              <ChevronRight className="h-6 w-6 text-secondary-600" />
            </button>
          </div>
        </div>

        {/* Research Focus Areas */}
        <div className="mt-16 text-center">
          <h3 className="text-2xl font-bold text-secondary-900 mb-8">
            Research Focus Areas
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white/60 backdrop-blur-sm rounded-xl p-6">
              <h4 className="text-lg font-semibold text-secondary-900 mb-3">
                AI-Driven Training Systems
              </h4>
              <p className="text-secondary-600 text-sm">
                Developing intelligent tutoring systems and adaptive learning platforms that enhance skill development through personalized feedback and real-time guidance.
              </p>
            </div>
            <div className="bg-white/60 backdrop-blur-sm rounded-xl p-6">
              <h4 className="text-lg font-semibold text-secondary-900 mb-3">
                Reinforcement Learning & Feedback
              </h4>
              <p className="text-secondary-600 text-sm">
                Creating reinforcement learning agents that provide real-time formative feedback to users in training simulations and educational environments.
              </p>
            </div>
            <div className="bg-white/60 backdrop-blur-sm rounded-xl p-6">
              <h4 className="text-lg font-semibold text-secondary-900 mb-3">
                Aviation & Simulation Training
              </h4>
              <p className="text-secondary-600 text-sm">
                Advancing pilot training through AI augmentation, user modeling, and adaptive feedback mechanisms in flight simulation environments.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResearchSection; 